import { memo, useEffect, useState, useRef } from "react";

/**
 * Floating "+$100" / "-$50" chip that rises and fades whenever the tracked
 * value changes. Pure presentational — the parent decides what value to track.
 *
 * Props:
 *   value    — the current number to watch (e.g. player.crypto_value)
 *   prefix   — optional symbol shown before the delta (default "$")
 *   label    — optional small caption next to the delta (e.g. "crypto")
 *   resetKey — when this value changes, prevRef is re-baselined WITHOUT
 *              firing a delta (used to avoid spurious chips when the active
 *              player rotates).
 */
function FloatingDelta({ value, prefix = "$", label, resetKey }) {
  const prevRef = useRef(value);
  const resetRef = useRef(resetKey);
  const [deltas, setDeltas] = useState([]); // [{id, amount}]

  // Re-baseline silently whenever the reset key changes.
  useEffect(() => {
    if (resetRef.current !== resetKey) {
      resetRef.current = resetKey;
      prevRef.current = value;
    }
  }, [resetKey, value]);

  useEffect(() => {
    const prev = prevRef.current;
    if (typeof value === "number" && typeof prev === "number" && value !== prev) {
      const amount = value - prev;
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setDeltas((d) => [...d, { id, amount }]);
      window.setTimeout(() => {
        setDeltas((d) => d.filter((x) => x.id !== id));
      }, 1700);
    }
    prevRef.current = value;
  }, [value]);

  if (deltas.length === 0) return null;

  return (
    <div
      className="pointer-events-none absolute left-1/2 -top-2 z-20"
      data-testid="floating-delta-layer"
      aria-hidden="true"
    >
      {deltas.map((d) => {
        const positive = d.amount >= 0;
        return (
          <span
            key={d.id}
            data-testid={`floating-delta-${positive ? "up" : "down"}`}
            className={`absolute left-0 top-0 -translate-x-1/2 animate-delta-rise rounded-full px-2.5 py-0.5 text-xs font-bold shadow-md ${
              positive
                ? "bg-[#00E676] text-white"
                : "bg-[#FF5A5F] text-white"
            }`}
            style={{ fontFamily: "Fredoka" }}
          >
            {positive ? "+" : "−"}
            {prefix}
            {Math.abs(d.amount)}
            {label ? (
              <span className="ml-1 text-[9px] font-semibold opacity-80">{label}</span>
            ) : null}
          </span>
        );
      })}
    </div>
  );
}

export default memo(FloatingDelta);
